import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator } from "lucide-react";

interface CalculatorRow {
  id: string;
  label: string;
  type: 'input' | 'calculated' | 'sum';
  formula?: string; // e.g., "field1 + field2" or references to other field ids
  category?: 'einnahmen' | 'ausgaben';
}

interface CalculatorFieldProps {
  rows: CalculatorRow[];
  value: Record<string, number>;
  onChange: (value: Record<string, number>) => void;
  showResult?: boolean;
  resultLabel?: string;
  className?: string;
}

export function CalculatorField({
  rows,
  value = {},
  onChange,
  showResult = true,
  resultLabel = "Ergebnis",
  className,
}: CalculatorFieldProps) {
  // Calculate derived values
  const calculateValue = (row: CalculatorRow): number => {
    if (row.type === 'input') {
      return value[row.id] || 0;
    }

    if (row.type === 'sum') {
      // Sum all rows of a specific category
      return rows
        .filter((r) => r.category === row.category && r.type === 'input')
        .reduce((sum, r) => sum + (value[r.id] || 0), 0);
    }

    if (row.type === 'calculated' && row.formula) {
      // Parse simple formulas like "einnahmen - ausgaben"
      try {
        const formulaParts = row.formula.split(/\s*([-+*/])\s*/);
        let result = 0;
        let operator = '+';

        for (const part of formulaParts) {
          if (['+', '-', '*', '/'].includes(part)) {
            operator = part;
          } else {
            const fieldValue = value[part] || 
              calculateValue(rows.find(r => r.id === part) || { id: '', label: '', type: 'input' });
            
            switch (operator) {
              case '+': result += fieldValue; break;
              case '-': result -= fieldValue; break;
              case '*': result *= fieldValue; break;
              case '/': result = fieldValue !== 0 ? result / fieldValue : 0; break;
            }
          }
        }
        return result;
      } catch {
        return 0;
      }
    }

    return 0;
  };

  const handleInputChange = (id: string, newValue: number | null) => {
    onChange({
      ...value,
      [id]: newValue || 0,
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Group rows by category
  const einnahmenRows = rows.filter((r) => r.category === 'einnahmen');
  const ausgabenRows = rows.filter((r) => r.category === 'ausgaben');
  const resultRows = rows.filter((r) => !r.category);

  const renderRow = (row: CalculatorRow) => {
    const calculatedValue = calculateValue(row);
    const isCalculated = row.type === 'calculated' || row.type === 'sum';

    return (
      <div
        key={row.id}
        className={cn(
          "flex items-center justify-between gap-4 py-2",
          isCalculated && "bg-muted/50 -mx-3 px-3 rounded"
        )}
      >
        <Label className="text-sm flex-1">{row.label}</Label>
        
        {isCalculated ? (
          <div className="flex items-center gap-2 text-right min-w-[120px]">
            <Calculator className="h-4 w-4 text-muted-foreground" />
            <span className={cn(
              "font-semibold",
              calculatedValue >= 0 ? "text-emerald-600" : "text-destructive"
            )}>
              {formatCurrency(calculatedValue)}
            </span>
          </div>
        ) : (
          <div className="relative min-w-[120px]">
            <Input
              type="number"
              value={value[row.id] || ''}
              onChange={(e) =>
                handleInputChange(
                  row.id,
                  e.target.value ? parseFloat(e.target.value) : null
                )
              }
              className="pr-8 h-9 text-right"
              placeholder="0,00"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              €
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderSection = (title: string, sectionRows: CalculatorRow[], bgColor?: string) => {
    if (sectionRows.length === 0) return null;
    
    const sumRow = rows.find(
      (r) => r.type === 'sum' && r.category === sectionRows[0]?.category
    );

    return (
      <div className={cn("p-3 rounded-lg", bgColor)}>
        <h4 className="font-semibold mb-3">{title}</h4>
        <div className="space-y-1">
          {sectionRows.filter(r => r.type === 'input').map(renderRow)}
        </div>
        {sumRow && (
          <div className="mt-3 pt-3 border-t border-border">
            {renderRow(sumRow)}
          </div>
        )}
      </div>
    );
  };

  // Calculate final result
  const resultRow = resultRows.find(r => r.type === 'calculated');
  const finalResult = resultRow ? calculateValue(resultRow) : 0;

  return (
    <div className={cn("space-y-4", className)}>
      {renderSection("Einnahmen", einnahmenRows, "bg-emerald-50 dark:bg-emerald-950/20")}
      {renderSection("Ausgaben", ausgabenRows, "bg-rose-50 dark:bg-rose-950/20")}
      
      {showResult && resultRow && (
        <div className="p-4 bg-primary/10 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-lg">{resultLabel}</span>
            <span className={cn(
              "text-2xl font-bold",
              finalResult >= 0 ? "text-emerald-600" : "text-destructive"
            )}>
              {formatCurrency(finalResult)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
